/**
 * Tree merge — Merkle-based directory merge algorithm (§11 v3 §8)
 *
 * Strategy 0: .db files → page-level merge (§94 §2)
 *
 * Compares two directory trees (local vs remote) by their root hashes.
 * When hashes match, the entire subtree is skipped (O(1)).
 * When hashes differ, recursively descends to find changed entries.
 *
 * The merge is name-based in v3 (no ID-based rename tracking yet).
 * Node IDs are stored for future CRDT use but not active in this version.
 *
 * Conflict resolution (per-entry):
 *   - Only local changed → take local
 *   - Only remote changed → take remote
 *   - Both changed:
 *     - Files: text → 3-way merge, binary → mtime wins (remote tiebreak)
 *     - Directories: recursive merge
 *
 * Returns: merged tree as a flat file list (backward-compatible with existing engine)
 *          and the new rootDirHash + directory objects for the merged tree.
 */

import type { DirEntry, DirEntryFile, DirEntryDir, DirectoryObject, DirObjectStore, DirTreeCodecDeps } from './codec/dirTree'
import { decryptDirObject, encryptDirObject, createDirObject } from './codec/dirTree'
import type { ContentRef } from './types'
import { mergeText, mergeChunkedBinary, type ChunkRef } from './mergeUtils'

// ── Types ─────────────────────────────────────────────────────────

export interface TreeMergeInput {
    /** Hash of the local tree's root directory */
    localRootHash: string | null
    /** Hash of the remote tree's root directory */
    remoteRootHash: string | null
    /** Hash of the base (common ancestor) tree's root directory */
    baseRootHash: string | null
}

export interface TreeMergeResult {
    /** Hash of the merged root directory */
    mergedRootHash: string | null
    /** All new/changed directory objects produced by the merge */
    newDirObjects: Map<string, Uint8Array>
    /** Paths where merge conflicts occurred (content conflicts) */
    conflictPaths: string[]
    /** Whether any local change was detected */
    anyLocalChange: boolean
    /** Flat file list from merged tree (for backward compat with existing workspace) */
    mergedFiles: Map<string, FlatFileEntry>
}

export interface FlatFileEntry {
    path: string
    entry: DirEntryFile
}

/**
 * I/O interface for content-level merge.
 * Pure I/O — no policy decisions. Enables text 3-way merge and chunk-level binary merge.
 * Without it, computeTreeMerge falls back to policy-based winner selection.
 */
export interface TreeMergeIO {
    /** Read file content as text */
    readContentText(content: ContentRef): Promise<string>
    /** Read file content as bytes */
    readContentBytes(content: ContentRef): Promise<Uint8Array | null>
    /** Read chunk refs from a v3 manifest (for chunk-level binary merge) */
    readManifestChunks(content: ContentRef): Promise<ChunkRef[] | null>
    /** Encode merged content → new CAS objects */
    encodeContent(bytes: Uint8Array): Promise<{ content: ContentRef; size: number }>
    /** Rebuild a v3 manifest from merged chunk sequence */
    rebuildManifest(chunks: ChunkRef[], baseContent: ContentRef): Promise<{ content: ContentRef; totalPlainSize: number }>

    /** Read sqlite pages as Map<pgno, pageData> (§94 §2.3) */
    readSqlitePages?(content: ContentRef): Promise<Map<number, Uint8Array> | null>
    /** Encode merged sqlite DB → new CAS objects */
    encodeSqliteDb?(pages: Map<number, Uint8Array>, pageSize: number): Promise<{ content: ContentRef; size: number }>
    /** Read sqlite manifest metadata only: pgno→hash (§94 §3.1 — TB-scale) */
    readSqliteManifest?(content: ContentRef): Promise<Map<number, string> | null>
    /** Read only specific pages by pgno from a page-aligned manifest (§94 §3.2 — TB-scale) */
    readSqlitePagesByPgno?(content: ContentRef, pgnos: Set<number>): Promise<Map<number, Uint8Array> | null>
    /** Delta-encode: reference base manifest, only encrypt changed pages (§94 §3.3 — TB-scale) */
    encodeSqliteDeltaPages?(changed: Map<number, Uint8Array>, baseContent: ContentRef, pageSize: number, nTruncate?: number): Promise<{ content: ContentRef; size: number }>
}

/**
 * How to resolve text merge conflicts (§8 §3.1).
 *
 *   'remote_wins'      — On true conflict, discard local edits and use remote text.
 *                         Local version is preserved in commit history via merge parents.
 *   'local_wins'       — On true conflict, keep local text and discard remote edits.
 *   'conflict_markers' — Embed <<<<<<< / ======= / >>>>>>> markers in the merged text.
 *                         Consumer must resolve markers (e.g. via UI) before commit.
 */
export type TextConflictPolicy = 'remote_wins' | 'local_wins' | 'conflict_markers'

/**
 * How to resolve binary merge conflicts (§8.2).
 *
 *   'remote_wins' — Always take remote file on conflict.
 *   'local_wins'  — Always take local file on conflict.
 *   'mtime_wins'  — Latest mtime wins; remote wins on tie (original §8.2 behavior).
 */
export type BinaryConflictPolicy = 'remote_wins' | 'local_wins' | 'mtime_wins'

/**
 * Unified merge conflict resolution policy.
 * Controls how both text and binary file conflicts are resolved.
 *
 * Defaults: text='remote_wins', binary='remote_wins'
 */
export interface MergeConflictPolicy {
    /** Text conflict resolution. @default 'remote_wins' */
    text?: TextConflictPolicy
    /** Binary conflict resolution. @default 'remote_wins' */
    binary?: BinaryConflictPolicy
}

/**
 * Options for computeTreeMerge.
 * Separates I/O (how to read/write content) from policy (how to resolve conflicts).
 */
export interface TreeMergeOptions {
    /** Content-level I/O. Without it, conflicts fall back to policy-based winner selection. */
    io?: TreeMergeIO
    /**
     * Conflict resolution policy for text and binary files.
     * @default { text: 'remote_wins', binary: 'remote_wins' }
     */
    conflictPolicy?: MergeConflictPolicy
}

function maxSqlitePgno<T>(pages: Map<number, T>): number {
    let max = 0
    for (const pgno of pages.keys()) {
        if (pgno > max) max = pgno
    }
    return max
}

function resolveSqlitePageCount(
    basePages: Map<number, unknown>,
    localPages: Map<number, unknown>,
    remotePages: Map<number, unknown>,
): number {
    const basePageCount = maxSqlitePgno(basePages)
    const localPageCount = maxSqlitePgno(localPages)
    const remotePageCount = maxSqlitePgno(remotePages)
    if (localPageCount === remotePageCount) return localPageCount

    const localCountChanged = localPageCount !== basePageCount
    const remoteCountChanged = remotePageCount !== basePageCount
    if (localCountChanged && !remoteCountChanged) return localPageCount
    if (!localCountChanged && remoteCountChanged) return remotePageCount
    if (!localCountChanged && !remoteCountChanged) return basePageCount

    return Math.max(localPageCount, remotePageCount)
}

// ── Core ──────────────────────────────────────────────────────────

const MAX_MERGE_DEPTH = 256

/**
 * Merge two directory trees with an optional base.
 *
 * Hash-skip optimization: when localHash === remoteHash, the entire subtree
 * is identical — return immediately without fetching or comparing entries.
 */
export async function computeTreeMerge(
    input: TreeMergeInput,
    store: DirObjectStore,
    deps: DirTreeCodecDeps,
    options?: TreeMergeOptions,
): Promise<TreeMergeResult> {
    const io = options?.io
    const policy: MergeConflictPolicy = options?.conflictPolicy ?? {}
    const newDirObjects = new Map<string, Uint8Array>()
    const conflictPaths: string[] = []
    const mergedFiles = new Map<string, FlatFileEntry>()
    let anyLocalChange = false
    // Cyclic DAG protection: track visited directory hashes during merge.
    // Malicious actors could forge CAS hashes pointing to ancestors → infinite loops.
    const visited = new Set<string>()

    // Base case: both null → nothing to merge
    if (!input.localRootHash && !input.remoteRootHash) {
        return { mergedRootHash: null, newDirObjects, conflictPaths, anyLocalChange, mergedFiles }
    }

    // Only one side exists → take it
    if (input.localRootHash && !input.remoteRootHash) {
        anyLocalChange = true
        await flattenTree('/', input.localRootHash, store, deps, mergedFiles, 0)
        return { mergedRootHash: input.localRootHash, newDirObjects, conflictPaths, anyLocalChange, mergedFiles }
    }

    if (!input.localRootHash && input.remoteRootHash) {
        await flattenTree('/', input.remoteRootHash, store, deps, mergedFiles, 0)
        return { mergedRootHash: input.remoteRootHash, newDirObjects, conflictPaths, anyLocalChange: false, mergedFiles }
    }

    // Both exist — recurse
    const mergedRootHash = await mergeDir(
        '/',
        input.localRootHash!,
        input.remoteRootHash!,
        input.baseRootHash,
        store,
        deps,
        newDirObjects,
        conflictPaths,
        mergedFiles,
        { anyLocalChange: false },
        0,
        visited,
        io,
        policy,
    )

    return {
        mergedRootHash,
        newDirObjects,
        conflictPaths,
        anyLocalChange: conflictPaths.length > 0 || mergedRootHash !== input.remoteRootHash,
        mergedFiles,
    }
}

// ── Recursive merge ──────────────────────────────────────────────

async function mergeDir(
    path: string,
    localHash: string,
    remoteHash: string,
    baseHash: string | null,
    store: DirObjectStore,
    deps: DirTreeCodecDeps,
    newDirObjects: Map<string, Uint8Array>,
    conflictPaths: string[],
    mergedFiles: Map<string, FlatFileEntry>,
    changeTracker: { anyLocalChange: boolean },
    depth: number,
    visited: Set<string>,
    io?: TreeMergeIO,
    policy?: MergeConflictPolicy,
): Promise<string> {
    if (depth > MAX_MERGE_DEPTH) throw new Error('Tree merge exceeded maximum depth')

    // Cyclic DAG protection: detect if we're visiting a hash we've already entered
    const mergeKey = `${localHash}|${remoteHash}`
    if (visited.has(mergeKey)) throw new Error(`Cyclic directory graph detected at ${path}`)
    visited.add(mergeKey)

    try {
        // ── Hash-skip: identical subtrees → skip entirely ──────────
        if (localHash === remoteHash) {
            // Both sides identical — use either one, flatten for backward compat
            await flattenTree(path, localHash, store, deps, mergedFiles, depth)
            return localHash
        }

        // ── Both unchanged from base → use either (both same as base) ──
        if (baseHash && localHash === baseHash) {
            // Local unchanged, remote changed → take remote
            await flattenTree(path, remoteHash, store, deps, mergedFiles, depth)
            return remoteHash
        }
        if (baseHash && remoteHash === baseHash) {
            // Remote unchanged, local changed → take local
            changeTracker.anyLocalChange = true
            await flattenTree(path, localHash, store, deps, mergedFiles, depth)
            return localHash
        }

        // ── Both changed — recurse into entries ────────────────────
        const [localDir, remoteDir, baseDir] = await Promise.all([
            fetchDir(localHash, store, deps),
            fetchDir(remoteHash, store, deps),
            baseHash ? fetchDir(baseHash, store, deps) : null,
        ])

        // Build name → entry maps
        const localByName = new Map(localDir.entries.map(e => [e.name, e]))
        const remoteByName = new Map(remoteDir.entries.map(e => [e.name, e]))
        const baseByName = baseDir ? new Map(baseDir.entries.map(e => [e.name, e])) : null

        const allNames = new Set([...localByName.keys(), ...remoteByName.keys()])
        const mergedEntries: DirEntry[] = []

        for (const name of allNames) {
            const local = localByName.get(name)
            const remote = remoteByName.get(name)
            const base = baseByName?.get(name) ?? null
            const entryPath = path === '/' ? `/${name}` : `${path}/${name}`

            if (local && remote) {
                // Both sides have this entry
                if (local.kind === 'directory' && remote.kind === 'directory') {
                    // Both are directories → recursive merge
                    const mergedChildHash = await mergeDir(
                        entryPath,
                        (local as DirEntryDir).dirHash,
                        (remote as DirEntryDir).dirHash,
                        base?.kind === 'directory' ? (base as DirEntryDir).dirHash : null,
                        store, deps, newDirObjects, conflictPaths, mergedFiles, changeTracker, depth + 1, visited, io, policy,
                    )
                    mergedEntries.push({
                        ...remote,
                        kind: 'directory',
                        dirHash: mergedChildHash,
                        mtime: Math.max(local.mtime, remote.mtime),
                    } as DirEntryDir)
                } else if (local.kind === 'file' && remote.kind === 'file') {
                    // Both are files — use content hash comparison
                    const localFile = local as DirEntryFile
                    const remoteFile = remote as DirEntryFile
                    const localContentHash = getFileContentHash(localFile)
                    const remoteContentHash = getFileContentHash(remoteFile)

                    if (localContentHash === remoteContentHash) {
                        // Same content — merge metadata (latest mtime, preserve ctime)
                        mergedEntries.push({
                            ...remoteFile,
                            mtime: Math.max(localFile.mtime, remoteFile.mtime),
                            ctime: Math.min(localFile.ctime, remoteFile.ctime),
                        })
                        const flatPath = entryPath.replace(/^\//, '/')
                        mergedFiles.set(flatPath, { path: flatPath, entry: { ...remoteFile, name, mtime: Math.max(localFile.mtime, remoteFile.mtime) } })
                    } else {
                        // Content differs — check base
                        const baseFile = base?.kind === 'file' ? base as DirEntryFile : null
                        const baseContentHash = baseFile ? getFileContentHash(baseFile) : null

                        if (baseContentHash === localContentHash) {
                            // Local unchanged, remote changed → take remote
                            mergedEntries.push(remoteFile)
                            mergedFiles.set(entryPath, { path: entryPath, entry: remoteFile })
                        } else if (baseContentHash === remoteContentHash) {
                            // Remote unchanged, local changed → take local
                            changeTracker.anyLocalChange = true
                            mergedEntries.push(localFile)
                            mergedFiles.set(entryPath, { path: entryPath, entry: localFile })
                        } else {
                            // Both changed from base — attempt content merge
                            if (io) {
                                // Strategy 0: SQLite row-level merge (§94 §2, upgraded from page-level)
                                if (entryPath.endsWith('.db') && baseFile && io.readSqlitePages && io.encodeSqliteDb) {
                                    const { SqliteThreeWayMerger } = await import('./sqliteThreeWayMerger')
                                    let sqliteMerged = false

                                    // TB-scale path: manifest-level diff → selective fetch → delta encode
                                    if (io.readSqliteManifest && io.readSqlitePagesByPgno && io.encodeSqliteDeltaPages) {
                                        const [baseMeta, localMeta, remoteMeta] = await Promise.all([
                                            io.readSqliteManifest(baseFile.content),
                                            io.readSqliteManifest(localFile.content),
                                            io.readSqliteManifest(remoteFile.content),
                                        ])
                                        if (baseMeta && localMeta && remoteMeta) {
                                            // Hash-diff: identify changed pgnos (0 page data loaded)
                                            const localChanged = new Set<number>()
                                            const remoteChanged = new Set<number>()
                                            for (const [pgno, hash] of localMeta) {
                                                if (baseMeta.get(pgno) !== hash) localChanged.add(pgno)
                                            }
                                            for (const [pgno, hash] of remoteMeta) {
                                                if (baseMeta.get(pgno) !== hash) remoteChanged.add(pgno)
                                            }
                                            // New pages not in base
                                            for (const pgno of localMeta.keys()) if (!baseMeta.has(pgno)) localChanged.add(pgno)
                                            for (const pgno of remoteMeta.keys()) if (!baseMeta.has(pgno)) remoteChanged.add(pgno)
                                            // Pages present in base but absent from a branch are truncation/deletion changes.
                                            for (const pgno of baseMeta.keys()) {
                                                if (!localMeta.has(pgno)) localChanged.add(pgno)
                                                if (!remoteMeta.has(pgno)) remoteChanged.add(pgno)
                                            }

                                            const allChanged = new Set([...localChanged, ...remoteChanged])
                                            if (allChanged.size > 0) {
                                                // SQLite page 1 is the database control page. A WAL commit can grow
                                                // the logical page count without changing page 1 bytes, but the sparse
                                                // merge still has to rewrite header offset 28 to the merged page count.
                                                allChanged.add(1)
                                                // Fetch ONLY changed pages — O(k) not O(n)
                                                const [bP, lP, rP] = await Promise.all([
                                                    io.readSqlitePagesByPgno(baseFile.content, allChanged),
                                                    io.readSqlitePagesByPgno(localFile.content, allChanged),
                                                    io.readSqlitePagesByPgno(remoteFile.content, allChanged),
                                                ])
                                                if (bP && lP && rP) {
                                                    const pageSize = bP.values().next().value?.length ?? lP.values().next().value?.length ?? 8192
                                                    const totalPageCount = resolveSqlitePageCount(baseMeta, localMeta, remoteMeta)
                                                    const sqliteResult = await SqliteThreeWayMerger.mergePages(
                                                        bP, lP, rP, pageSize, totalPageCount)

                                                    if (sqliteResult.status === 'schema-conflict') {
                                                        const merged = await mergeFileContent(
                                                            entryPath, localFile, remoteFile, baseFile!, io, policy)
                                                        mergedEntries.push(merged.entry)
                                                        mergedFiles.set(entryPath, { path: entryPath, entry: merged.entry })
                                                        if (merged.conflicted) conflictPaths.push(entryPath)
                                                        if (merged.isLocalWinner) changeTracker.anyLocalChange = true
                                                    } else {
                                                        const encoded = await io.encodeSqliteDeltaPages(
                                                            sqliteResult.mergedPages ?? new Map(), baseFile.content, pageSize, sqliteResult.pageCount ?? totalPageCount)
                                                        const mergedEntry: DirEntryFile = {
                                                            ...remoteFile,
                                                            content: encoded.content,
                                                            size: encoded.size,
                                                            mtime: Math.max(localFile.mtime, remoteFile.mtime),
                                                        }
                                                        mergedEntries.push(mergedEntry)
                                                        mergedFiles.set(entryPath, { path: entryPath, entry: mergedEntry })
                                                        if (sqliteResult.status === 'row-conflict') conflictPaths.push(entryPath)
                                                        changeTracker.anyLocalChange = true
                                                    }
                                                    sqliteMerged = true
                                                }
                                            } else {
                                                // No changes detected — both sides identical to base
                                                mergedEntries.push(remoteFile)
                                                mergedFiles.set(entryPath, { path: entryPath, entry: remoteFile })
                                                sqliteMerged = true
                                            }
                                        }
                                    }

                                    // Fallback: full-DB read (non-page-aligned or inline content)
                                    if (!sqliteMerged) {
                                        const [basePages, localPages, remotePages] = await Promise.all([
                                            io.readSqlitePages(baseFile.content),
                                            io.readSqlitePages(localFile.content),
                                            io.readSqlitePages(remoteFile.content),
                                        ])
                                        if (basePages && localPages && remotePages) {
                                            const anyPage = basePages.values().next().value
                                            const pageSize = anyPage?.length ?? 4096
                                            const totalPageCount = resolveSqlitePageCount(basePages, localPages, remotePages)
                                            const sqliteResult = await SqliteThreeWayMerger.mergePages(
                                                basePages, localPages, remotePages, pageSize, totalPageCount)

                                            if (sqliteResult.status === 'schema-conflict') {
                                                const merged = await mergeFileContent(
                                                    entryPath, localFile, remoteFile, baseFile!, io, policy)
                                                mergedEntries.push(merged.entry)
                                                mergedFiles.set(entryPath, { path: entryPath, entry: merged.entry })
                                                if (merged.conflicted) conflictPaths.push(entryPath)
                                                if (merged.isLocalWinner) changeTracker.anyLocalChange = true
                                            } else {
                                                // Assemble full DB: base + mergePages delta
                                                const fullPages = new Map(basePages)
                                                if (sqliteResult.mergedPages) {
                                                    for (const [pgno, page] of sqliteResult.mergedPages)
                                                        fullPages.set(pgno, page)
                                                }
                                                const mergedPageCount = sqliteResult.pageCount ?? totalPageCount
                                                for (const pgno of [...fullPages.keys()]) {
                                                    if (pgno > mergedPageCount) fullPages.delete(pgno)
                                                }
                                                const encoded = await io.encodeSqliteDb(fullPages, pageSize)
                                                const mergedEntry: DirEntryFile = {
                                                    ...remoteFile,
                                                    content: encoded.content,
                                                    size: encoded.size,
                                                    mtime: Math.max(localFile.mtime, remoteFile.mtime),
                                                }
                                                mergedEntries.push(mergedEntry)
                                                mergedFiles.set(entryPath, { path: entryPath, entry: mergedEntry })
                                                if (sqliteResult.status === 'row-conflict') conflictPaths.push(entryPath)
                                                changeTracker.anyLocalChange = true
                                            }
                                        } else {
                                            // Fallback to text/binary merge if pages unreadable
                                            const merged = await mergeFileContent(
                                                entryPath, localFile, remoteFile, baseFile, io, policy,
                                            )
                                            mergedEntries.push(merged.entry)
                                            mergedFiles.set(entryPath, { path: entryPath, entry: merged.entry })
                                            if (merged.conflicted) conflictPaths.push(entryPath)
                                            if (merged.isLocalWinner) changeTracker.anyLocalChange = true
                                        }
                                    }
                                } else {
                                    const merged = await mergeFileContent(
                                        entryPath, localFile, remoteFile, baseFile, io, policy,
                                    )
                                    mergedEntries.push(merged.entry)
                                    mergedFiles.set(entryPath, { path: entryPath, entry: merged.entry })
                                    if (merged.conflicted) conflictPaths.push(entryPath)
                                    if (merged.isLocalWinner) changeTracker.anyLocalChange = true
                                }
                            } else {
                                // No IO — apply binary policy for winner selection
                                const winner = resolveBinaryConflict(localFile, remoteFile, policy)
                                mergedEntries.push(winner.entry)
                                mergedFiles.set(entryPath, { path: entryPath, entry: winner.entry })
                                conflictPaths.push(entryPath)
                                if (winner.isLocalWinner) changeTracker.anyLocalChange = true
                            }
                        }
                    }
                } else {
                    // Kind mismatch (file ↔ directory) — Top-Tier Structural Conflict Resolution (Directory Wins, File Renamed)
                    const localIsFile = local.kind === 'file'
                    const fileEntry = (localIsFile ? local : remote) as DirEntryFile
                    const dirOrSymEntry = localIsFile ? remote : local

                    // Generate conflict name for the file
                    const lastDot = fileEntry.name.lastIndexOf('.')
                    const ext = lastDot > 0 ? fileEntry.name.substring(lastDot) : ''
                    const nameWithoutExt = lastDot > 0 ? fileEntry.name.substring(0, lastDot) : fileEntry.name
                    const conflictedName = `${nameWithoutExt} (Conflicted file)${ext}`

                    const renamedFileEntry: DirEntryFile = {
                        ...fileEntry,
                        name: conflictedName,
                    }

                    mergedEntries.push(dirOrSymEntry, renamedFileEntry)

                    if (dirOrSymEntry.kind === 'directory') {
                        await flattenTree(entryPath, (dirOrSymEntry as DirEntryDir).dirHash, store, deps, mergedFiles, depth + 1)
                    }

                    const renamedPath = path === '/' ? `/${conflictedName}` : `${path}/${conflictedName}`
                    mergedFiles.set(renamedPath, { path: renamedPath, entry: renamedFileEntry })

                    conflictPaths.push(entryPath)
                    changeTracker.anyLocalChange = true
                }
            } else if (local && !remote) {
                // Only local has it
                if (base && baseByName?.has(name)) {
                    // Was in base, removed by remote → take remote's deletion (unless local modified)
                    const baseEntry = baseByName.get(name)!
                    const isModified = local.kind === 'file' && baseEntry.kind === 'file'
                        ? getFileContentHash(local as DirEntryFile) !== getFileContentHash(baseEntry as DirEntryFile)
                        : local.mtime !== baseEntry.mtime

                    if (isModified) {
                        // Local modified + remote deleted → conflict, keep local
                        mergedEntries.push(local)
                        conflictPaths.push(entryPath)
                        changeTracker.anyLocalChange = true
                        if (local.kind === 'file') {
                            mergedFiles.set(entryPath, { path: entryPath, entry: local as DirEntryFile })
                        } else {
                            await flattenTree(entryPath, (local as DirEntryDir).dirHash, store, deps, mergedFiles, depth + 1)
                        }
                    }
                    // Not modified → accept remote deletion (don't add to merged)
                } else {
                    // Not in base → local added it
                    changeTracker.anyLocalChange = true
                    mergedEntries.push(local)
                    if (local.kind === 'file') {
                        mergedFiles.set(entryPath, { path: entryPath, entry: local as DirEntryFile })
                    } else {
                        await flattenTree(entryPath, (local as DirEntryDir).dirHash, store, deps, mergedFiles, depth + 1)
                    }
                }
            } else if (!local && remote) {
                // Only remote has it
                if (base && baseByName?.has(name)) {
                    // Was in base, removed by local → take local's deletion (unless remote modified)
                    const baseEntry = baseByName.get(name)!
                    const isModified = remote.kind === 'file' && baseEntry.kind === 'file'
                        ? getFileContentHash(remote as DirEntryFile) !== getFileContentHash(baseEntry as DirEntryFile)
                        : remote.mtime !== baseEntry.mtime

                    if (isModified) {
                        // Remote modified + local deleted → conflict, keep remote
                        mergedEntries.push(remote)
                        conflictPaths.push(entryPath)
                        if (remote.kind === 'file') {
                            mergedFiles.set(entryPath, { path: entryPath, entry: remote as DirEntryFile })
                        } else {
                            await flattenTree(entryPath, (remote as DirEntryDir).dirHash, store, deps, mergedFiles, depth + 1)
                        }
                    }
                    // Not modified → accept local deletion (don't add to merged)
                } else {
                    // Not in base → remote added it
                    mergedEntries.push(remote)
                    if (remote.kind === 'file') {
                        mergedFiles.set(entryPath, { path: entryPath, entry: remote as DirEntryFile })
                    } else {
                        await flattenTree(entryPath, (remote as DirEntryDir).dirHash, store, deps, mergedFiles, depth + 1)
                    }
                }
            }
        }

        // Build merged directory object
        const mergedDir = createDirObject(mergedEntries)
        const { ciphertext, hash } = await encryptDirObject(mergedDir, deps)
        newDirObjects.set(hash, ciphertext)

        return hash
    } finally {
        visited.delete(mergeKey)
    }
}

// ── Content merge ────────────────────────────────────────────────

/** MIME types that support text 3-way merge */
const TEXT_MIMES = new Set([
    'text/plain', 'text/markdown', 'text/html', 'text/css', 'text/csv',
    'text/xml', 'text/yaml', 'text/x-yaml',
    'application/json', 'application/xml', 'application/xhtml+xml',
    'application/javascript', 'text/javascript', 'text/typescript',
])

function isTextMime(mime: string): boolean {
    return TEXT_MIMES.has(mime.split(';')[0].trim().toLowerCase()) || mime.startsWith('text/')
}

/**
 * Merge file content when both sides changed from base.
 *
 * Strategy ladder (top tier — fully exploits v3 features):
 *   1. Text files → line-level 3-way diff3 merge
 *   2. Chunked binary → chunk-level 3-way merge (LCS on hash sequences)
 *   3. Inline binary / no base → mtime LWW (remote tiebreak)
 */
async function mergeFileContent(
    path: string,
    localFile: DirEntryFile,
    remoteFile: DirEntryFile,
    baseFile: DirEntryFile | null,
    io: TreeMergeIO,
    policy?: MergeConflictPolicy,
): Promise<{ entry: DirEntryFile; conflicted: boolean; isLocalWinner: boolean }> {
    const isText = isTextMime(localFile.mime || '') && isTextMime(remoteFile.mime || '')

    // ── Strategy 1: Text 3-way diff3 merge ────────────────────
    if (isText && baseFile) {
        try {
            const [baseText, localText, remoteText] = await Promise.all([
                io.readContentText(baseFile.content),
                io.readContentText(localFile.content),
                io.readContentText(remoteFile.content),
            ])

            const result = mergeText(baseText, localText, remoteText)
            const textPolicy = policy?.text ?? 'remote_wins'

            let finalText: string
            let isLocalWinner = false
            if (!result.conflicted) {
                finalText = typeof result.merged === 'string' ? result.merged : new TextDecoder().decode(result.merged)
            } else {
                // Apply configured text conflict resolution policy
                switch (textPolicy) {
                    case 'remote_wins':
                        finalText = remoteText
                        break
                    case 'local_wins':
                        finalText = localText
                        isLocalWinner = true
                        break
                    case 'conflict_markers':
                        finalText = typeof result.merged === 'string' ? result.merged : new TextDecoder().decode(result.merged)
                        break
                }
            }

            const mergedBytes = new TextEncoder().encode(finalText)
            const encoded = await io.encodeContent(mergedBytes)

            const mergedEntry: DirEntryFile = {
                ...remoteFile,
                content: encoded.content,
                size: mergedBytes.length,
                mtime: Math.max(localFile.mtime, remoteFile.mtime),
                ctime: Math.min(localFile.ctime, remoteFile.ctime),
            }

            return { entry: mergedEntry, conflicted: result.conflicted, isLocalWinner }
        } catch {
            // Text merge failed — fall through to chunk merge or LWW
        }
    }

    // ── Strategy 2: Chunk-level 3-way merge (v3 manifests) ────
    if (baseFile && !isText) {
        try {
            const [baseChunks, localChunks, remoteChunks] = await Promise.all([
                io.readManifestChunks(baseFile.content),
                io.readManifestChunks(localFile.content),
                io.readManifestChunks(remoteFile.content),
            ])

            if (baseChunks && localChunks && remoteChunks) {
                const result = mergeChunkedBinary(baseChunks, localChunks, remoteChunks)
                const rebuilt = await io.rebuildManifest(result.chunks, remoteFile.content)

                const mergedEntry: DirEntryFile = {
                    ...remoteFile,
                    content: rebuilt.content,
                    size: rebuilt.totalPlainSize,
                    mtime: Math.max(localFile.mtime, remoteFile.mtime),
                    ctime: Math.min(localFile.ctime, remoteFile.ctime),
                }

                return { entry: mergedEntry, conflicted: result.conflicted, isLocalWinner: result.anyLocalChange }
            }
        } catch {
            // Chunk merge failed — fall through to LWW
        }
    }

    // ── Strategy 3: Binary conflict resolution (policy-aware) ──
    return resolveBinaryConflict(localFile, remoteFile, policy)
}

/**
 * Apply binary conflict policy to pick a winner.
 * Extracted as a shared helper — used by both the IO path (Strategy 3 fallback)
 * and the no-IO path (inline LWW) for consistent policy application.
 */
function resolveBinaryConflict(
    localFile: DirEntryFile,
    remoteFile: DirEntryFile,
    policy?: MergeConflictPolicy,
): { entry: DirEntryFile; conflicted: true; isLocalWinner: boolean } {
    const binaryPolicy = policy?.binary ?? 'remote_wins'
    let localWins: boolean
    switch (binaryPolicy) {
        case 'remote_wins':
            localWins = false
            break
        case 'local_wins':
            localWins = true
            break
        case 'mtime_wins':
            localWins = localFile.mtime > remoteFile.mtime
            break
    }
    return { entry: localWins ? localFile : remoteFile, conflicted: true, isLocalWinner: localWins }
}

// ── Helpers ───────────────────────────────────────────────────────

async function fetchDir(hash: string, store: DirObjectStore, deps: DirTreeCodecDeps): Promise<DirectoryObject> {
    const ciphertext = await store.get(hash)
    if (!ciphertext) throw new Error(`Directory object not found: ${hash}`)
    return decryptDirObject(ciphertext, deps)
}

function getFileContentHash(entry: DirEntryFile): string {
    return entry.content.type === 'inline' ? entry.content.hash : entry.content.manifestHash
}

/**
 * Flatten a directory tree into the mergedFiles map (recursive).
 * Used for hash-skip branches and one-sided merges.
 */
async function flattenTree(
    path: string,
    dirHash: string,
    store: DirObjectStore,
    deps: DirTreeCodecDeps,
    mergedFiles: Map<string, FlatFileEntry>,
    depth: number,
): Promise<void> {
    if (depth > MAX_MERGE_DEPTH) throw new Error('Tree flatten exceeded maximum depth')

    const dir = await fetchDir(dirHash, store, deps)

    for (const entry of dir.entries) {
        const entryPath = path === '/' ? `/${entry.name}` : `${path}/${entry.name}`

        if (entry.kind === 'file') {
            mergedFiles.set(entryPath, { path: entryPath, entry: entry as DirEntryFile })
        } else if (entry.kind === 'directory') {
            await flattenTree(entryPath, (entry as DirEntryDir).dirHash, store, deps, mergedFiles, depth + 1)
        }
        // symlinks: stored in entry metadata, not flattened to file list
    }
}
